import React from 'react'
import './JobAnalysisDisplay.css'
import { JobApplication } from './ApplicationWizard'

interface JobAnalysisDisplayProps {
  jobData: Partial<JobApplication>
  onEdit?: () => void
  isProcessing?: boolean
}

export const JobAnalysisDisplay: React.FC<JobAnalysisDisplayProps> = ({
  jobData,
  onEdit,
  isProcessing = false
}) => {
  if (isProcessing) {
    return (
      <div className="job-analysis-display">
        <div className="detailed-analysis">
          <h2>Analyzing Job Posting...</h2>
          <div className="status-message loading">
            <div className="spinner"></div>
            <span>This usually takes about 30-60 seconds.</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="job-analysis-display">
      <div className="detailed-analysis">
        <h2>{jobData.position || 'Job Analysis'}</h2>
        <p><strong>Company:</strong> {jobData.company || 'N/A'}</p>
        
        <div className="score-grid">
          <div className="score-item">
            <div className="score">{jobData.jobLevelConfidence ? Math.round(jobData.jobLevelConfidence * 100) : 'N/A'}%</div>
            <div>Fit Score</div>
          </div>
          <div className="score-item">
            <div className="score">{jobData.technologies?.length ? Math.min(100, jobData.technologies.length * 10) : 'N/A'}%</div>
            <div>Tech Fit</div>
          </div>
        </div>
        
        <div className="match-item">
          <h3>Experience Match</h3>
          <ul>
            <li>{jobData.requiredQualifications?.join(', ') || 'No technical skills analysis'}</li>
            <li>{jobData.optionalQualifications?.join(', ') || 'No domain fit analysis'}</li>
          </ul>
        </div>
        
                {jobData.potentialGaps && jobData.potentialGaps.length > 0 ? (
                  <div className="gaps-section">
                    <h3>Potential Gaps</h3>
                    <ul>
                      {jobData.potentialGaps.map((gap: string, index: number) => (
                        <li key={`gap-${index}`}>{gap}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
        
        {jobData.recommendations && jobData.recommendations.length > 0 ? (
          <div className="recommendations-section">
            <h3>Recommendations</h3>
            <ul>
              {jobData.recommendations.map((rec: string, index: number) => (
                <li key={`rec-${index}`}>{rec}</li>
              ))}
            </ul>
          </div>
        ) : null}
        
        {jobData.responsibilities && jobData.responsibilities.length > 0 ? (
          <div className="responsibilities-section">
            <h3>Key Responsibilities</h3>
            <ul>
              {jobData.responsibilities.map((responsibility, index) => (
                <li key={`resp-${index}`}>{responsibility}</li>
              ))}
            </ul>
          </div>
        ) : null}
        
        {jobData.technologies && jobData.technologies.length > 0 ? (
          <div className="programming-languages">
            <h3>Programming Languages & Technologies</h3>
            <p><strong>Required:</strong> {jobData.technologies.join(', ')}</p>
            <p><strong>Experience Level:</strong> {jobData.jobLevel || 'Not specified'}</p>
          </div>
        ) : null}
        
        {jobData.benefits && jobData.benefits.length > 0 ? (
          <div className="benefits-section">
            <h3>Benefits & Perks</h3>
            <ul>
              {jobData.benefits.map((benefit, index) => (
                <li key={`benefit-${index}`}>{benefit}</li>
              ))}
            </ul>
          </div>
        ) : null}
        
        {jobData.education && jobData.education.length > 0 ? (
          <div className="education-section">
            <h3>Education Requirements</h3>
            <ul>
              {jobData.education.map((edu, index) => (
                <li key={`edu-${index}`}>
                  <strong>{edu.degree}</strong> in {edu.field}
                  {edu.requirement && (
                    <span className={`requirement-badge ${edu.requirement}`}>
                      {edu.requirement}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        
        {jobData.coverLetterContent ? (
          <div className="cover-letter-content">
            <h3>Cover Letter Content</h3>
            <pre>{jobData.coverLetterContent}</pre>
          </div>
        ) : null}
        
        {jobData.jobSummary ? (
          <div className="job-summary-section">
            <h3>Job Summary</h3>
            <p>{jobData.jobSummary}</p>
          </div>
        ) : null}
        
                {jobData.nextSteps && jobData.nextSteps.length > 0 ? (
                  <div className="next-steps-section">
                    <h3>Next Steps</h3>
                    <ul>
                      {jobData.nextSteps.map((step: string, index: number) => (
                        <li key={`step-${index}`}>{step}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
        
        {onEdit && (
          <div className="analysis-actions">
            <button className="edit-details-btn" onClick={onEdit}>
              Edit Details
            </button>
          </div>
        )}
      </div>
    </div>
  )
}